import pluralize from 'pluralize'
import React, { useEffect, useState } from 'react'

import { IllustrationName } from '@/components/icons/Illustration'
import { useSummaryEditor } from '@/components/quotes/summary/summaryPopover'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { cn } from '../../lib/utils'
import { type Trailer, type Truck, useTrailers, useTrucks } from '../../models/equipment'
import { ChatActionWithValue } from '../../models/insuranceApplication'
import { isFleetReadonly, useCurrentUser } from '../../models/user'
import { TrailersTable } from '../TrailersTable'
import { TrucksTable } from '../TrucksTable'
import { IllustratedRow } from './ConfirmInfoCard'

export interface ChatAddEquipmentsProps {
  /** Review affordance rather than the step's own question — confirming then answers nothing. */
  edit: boolean
  slim?: boolean
  /** Answers the step. Left off wherever the roster is only being reviewed. */
  handleSubmit?: (data: ChatActionWithValue | FormData) => void
  type: 'trucks' | 'trailers'
  orgId: string | undefined
  readonly?: boolean
  /**
   * Extra action rendered under the roster row in slim mode, given the same opener
   * the row uses — mirrors ChatAddDrivers so the quotes page can add its own CTA.
   */
  renderFooter?: (open: () => void) => React.ReactNode
  /** Onboarding confirmation step only: requires the fields carriers need. */
  enforceCarrierRequired?: boolean
}

export function ChatAddEquipments({
  handleSubmit,
  edit,
  slim,
  type,
  orgId,
  readonly,
  renderFooter,
  enforceCarrierRequired,
}: ChatAddEquipmentsProps) {
  const {
    trucks: dbTrucks,
    isLoading: isLoadingTrucks,
    refetch: refetchTrucks,
  } = useTrucks({ orgId, shouldFetchAllPages: true })
  const {
    trailers: dbTrailers,
    isLoading: isLoadingTrailers,
    refetch: refetchTrailers,
  } = useTrailers({ orgId, shouldFetchAllPages: true })

  const [trucks, setTrucks] = useState<Truck[]>([])
  const [trailers, setTrailers] = useState<Trailer[]>([])
  // Closing the dialog also closes the summary popover it was opened from, where there is one.
  const dialog = useSummaryEditor()
  const { user } = useCurrentUser({ enabled: false })
  const isDisabled = readonly || isFleetReadonly(user)

  useEffect(() => setTrucks(dbTrucks), [dbTrucks])
  useEffect(() => setTrailers(dbTrailers), [dbTrailers])

  const items = type === 'trucks' ? trucks : trailers

  async function onConfirm() {
    await Promise.all([refetchTrucks(), refetchTrailers()])
    // Outside its own question this would answer whatever step the application is on.
    if (!edit) {
      handleSubmit?.({
        content: {
          component: 'ADD_EQUIPMENTS',
          content: ``,
          props: {
            disableEnteringAnimation: true,
            edit: true,
            type,
          },
        },
        value: {
          name: 'ADD_EQUIPMENTS',
          type,
        },
      })
    }
    dialog.close()
  }

  function renderHeader() {
    const isLoading = type === 'trucks' ? isLoadingTrucks : isLoadingTrailers
    const label = type === 'trucks' ? 'truck' : 'trailer'

    if (isLoading) return `Loading ${type}...`

    const found = edit || !items.length ? '' : 'found'
    return `${items.length} active ${pluralize(label, items.length)} ${found}`
  }

  const row = (
    <IllustratedRow
      illustration={type === 'trucks' ? IllustrationName.TRUCK : IllustrationName.TRAILER}
      label={renderHeader()}
      action={
        !isDisabled &&
        // Nothing on file to edit — the host's own CTA ("Add trailer") is the way in instead.
        (edit ? (
          (items.length > 0 || !renderFooter) && <Button variant={slim ? 'outline' : 'link'}>Edit</Button>
        ) : (
          <Button>{items.length === 0 ? 'Add' : 'Review'}</Button>
        ))
      }
    />
  )

  const dialogElement = (
    <Dialog open={dialog.isOpen} onOpenChange={dialog.setOpen}>
      <DialogContent size="full">
        <div>
          {!orgId || (type === 'trucks' ? isLoadingTrucks : isLoadingTrailers) ? (
            <div className="text-muted-foreground py-10 text-center text-sm">Loading {type}…</div>
          ) : type === 'trucks' ? (
            <TrucksTable
              trucks={trucks}
              setTrucks={setTrucks}
              handleSubmit={() => onConfirm()}
              orgId={orgId}
              enforceCarrierRequired={enforceCarrierRequired}
              footer={
                <div className="flex items-center justify-center gap-2">
                  <Button variant="link" onClick={dialog.close}>
                    Cancel
                  </Button>
                </div>
              }
            />
          ) : (
            <TrailersTable
              trailers={trailers}
              setTrailers={setTrailers}
              handleSubmit={() => onConfirm()}
              orgId={orgId}
              enforceCarrierRequired={enforceCarrierRequired}
              footer={
                <div className="flex items-center justify-center gap-2">
                  <Button variant="link" onClick={dialog.close}>
                    Cancel
                  </Button>
                </div>
              }
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )

  if (slim) {
    return (
      <>
        <div
          className={cn('flex w-full cursor-pointer', isDisabled && 'cursor-auto opacity-50')}
          onClick={isDisabled ? undefined : dialog.open}
        >
          {row}
        </div>
        {!isDisabled && renderFooter?.(dialog.open)}
        {dialogElement}
      </>
    )
  }

  return (
    <>
      <div
        className={cn(
          'mt-6 flex w-full cursor-pointer items-center justify-end gap-2 font-semibold',
          isDisabled && 'cursor-auto opacity-50'
        )}
        onClick={isDisabled ? undefined : dialog.open}
      >
        <Card className="w-full bg-transparent">
          <CardContent>{row}</CardContent>
        </Card>
      </div>
      {dialogElement}
    </>
  )
}
